import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import { MouseEvent, useState } from 'react';
import { Button, Confirm, useNotify, useRecordContext, useRefresh, useUpdate } from 'react-admin';

type LineItemArchiveRecord = {
  archivedAt?: string | null;
  id: string;
  lineItemNumber?: number | null;
  name?: string | null;
};

const formatLineItemArchiveLabel = (record: LineItemArchiveRecord) => {
  if (record.lineItemNumber && record.name) return `#${record.lineItemNumber} ${record.name}`;
  if (record.lineItemNumber) return `#${record.lineItemNumber}`;
  return record.name ?? 'this line item';
};

export const LineItemArchiveButton = () => {
  const record = useRecordContext<LineItemArchiveRecord>();
  const notify = useNotify();
  const refresh = useRefresh();
  const [open, setOpen] = useState(false);

  const isArchived = Boolean(record?.archivedAt);
  const action = isArchived ? 'Restore' : 'Archive';

  const [update, { isLoading }] = useUpdate(
    'lineItems',
    undefined,
    {
      onSuccess: () => {
        notify(`Line item ${action.toLowerCase()}d successfully`, { type: 'success' });
        refresh();
      },
      onError: error => {
        notify(`Error: ${error}`, { type: 'warning' });
      },
    },
  );

  if (!record) {
    return null;
  }

  const handleClick = (event: MouseEvent) => {
    event.stopPropagation();
    setOpen(true);
  };

  return (
    <>
      <Button label={action} onClick={handleClick} disabled={isLoading} color={isArchived ? 'info' : 'error'}>
        {isArchived ? <UnarchiveIcon /> : <ArchiveIcon />}
      </Button>
      <Confirm
        isOpen={open}
        loading={isLoading}
        title={`Confirm Line Item ${action}`}
        content={`Are you sure you want to ${action.toLowerCase()} ${formatLineItemArchiveLabel(record)}?`}
        onConfirm={() => {
          update('lineItems', {
            id: record.id,
            data: { archivedAt: isArchived ? null : new Date().toISOString() },
            previousData: record,
          });
          setOpen(false);
        }}
        onClose={() => setOpen(false)}
      />
    </>
  );
};
